import React, { useMemo, useSyncExternalStore } from 'react';

import { useIntl, type IntlShape } from 'react-intl';

import type { SelectFilterOption } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys } from '../../../../../common/constants/product-keys';
import { FilterProductIcon } from '../../../../../common/ui/filters/filter-product-icon';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter from '../../../../../common/ui/filters/select-filter';
import { formatAvatarOptionLabel } from '../../../../../common/ui/filters/select-filter/option-label';
import { serveAppendDeactivatedAccountToContributorList } from '../../../../../common/utils/experiments';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import {
	useAvailableProducts,
	useFiltersDiscoverability2Enabled,
	usePrimaryProduct,
	useQueryParams,
} from '../../../../../controllers/store/bootstrap';
import { filter as contributorFilter } from '../../../../../controllers/store/search/filters/contributor';
import {
	findMinedDeactivatedUsers,
	getMinedDeactivatedUsers,
	getMinedDeactivatedUsersVersion,
	subscribeToMinedDeactivatedUsers,
	type MinedDeactivatedUser,
} from '../../../../../controllers/store/search/filters/contributor/mined-deactivated-users';

import { i18n } from './messages';

const EMPTY_OPTIONS: SelectFilterOption[] = [];
const EMPTY_USERS: MinedDeactivatedUser[] = [];
const NOOP_SUBSCRIBE = (): (() => void) => () => {};
const DISABLED_SNAPSHOT = (): number => 0;

const toDeactivatedOption = (
	user: MinedDeactivatedUser,
	formatMessage: IntlShape['formatMessage'],
): SelectFilterOption => ({
	isSelected: false,
	trackingKey: user.accountId,
	value: user.accountId,
	queryParamValue: user.accountId,
	isSuggested: true,
	isDefault: false,
	avatarUrl: user.picture || undefined,
	label: formatMessage(i18n.confluenceFilterContributorDeactivatedLabel, {
		userName: user.name || formatMessage(i18n.confluenceFilterContributorUnknownUser),
	}),
	metadata: { isDeactivated: true },
	product: ProductKeys.Confluence,
});

// Deactivated users are removed from URS, so the contributor lookup never returns them. Instead we
// mine them from the search results on the page (their owner/author `accountStatus`). This single
// helper is the only place that applies that mined data to what the filter renders:
//
//   1. Decorate — any displayed option matching a mined deactivated user gets the "(Deactivated)"
//      label, avatar and metadata. Keeps the affordance visible in every render path (selected
//      chip, selected group, lookup list) no matter how the option entered the store.
//   2. Inject (lookup list only) — deactivated users matching the typed query that URS didn't
//      return are appended so they can be selected, reflecting their current selection state.
const applyDeactivatedUsers = (
	optionsToRender: SelectFilterOption[],
	deactivatedUsers: MinedDeactivatedUser[],
	formatMessage: IntlShape['formatMessage'],
	inject?: { matchingUsers: MinedDeactivatedUser[]; selectedValues: Set<string> },
): SelectFilterOption[] => {
	if (deactivatedUsers.length === 0) {
		return optionsToRender;
	}
	const usersByValue = new Map(deactivatedUsers.map((user) => [user.accountId, user]));

	const decorated = optionsToRender.map((option) => {
		const minedUser = usersByValue.get(option.value);
		if (!minedUser) {
			return option;
		}
		const asDeactivated = toDeactivatedOption(minedUser, formatMessage);
		return {
			...option,
			label: asDeactivated.label,
			avatarUrl: option.avatarUrl ?? asDeactivated.avatarUrl,
			metadata: { ...option.metadata, ...asDeactivated.metadata },
		};
	});

	if (!inject) {
		return decorated;
	}

	const seen = new Set(decorated.map((option) => option.value));
	const injected = inject.matchingUsers
		.filter((user) => !seen.has(user.accountId))
		.map((user) => ({
			...toDeactivatedOption(user, formatMessage),
			isSelected: inject.selectedValues.has(user.accountId),
		}));
	return [...decorated, ...injected];
};

const ContributorFilter = ({
	isFilterInDropdown,
	showPrimaryProductIcon,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
	onClearOptions,
}: FilterProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [primaryProduct] = usePrimaryProduct();
	const availableProducts = useAvailableProducts();
	const [queryParams] = useQueryParams();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [
		filterData,
		{ loadInitialOptions, setLookupInput, setOption, clearLookupInput, clearOptions },
	] = useSelectFilter({
		filterId: contributorFilter.id,
	});

	const isLoading = filterData?.isLoading ?? false;
	const rawSuggestedOptions = filterData?.suggestedOptions ?? EMPTY_OPTIONS;
	const rawSelectedOptions = filterData?.selectedOptions ?? EMPTY_OPTIONS;
	const rawOptions = filterData?.options ?? EMPTY_OPTIONS;
	const isLookupDefined = filterData?.isLookupDefined ?? false;
	const lookupInput = filterData?.lookupInput;
	const isLookupActive = filterData?.isLookupActive ?? false;
	const isDeactivatedContributorEnabled = serveAppendDeactivatedAccountToContributorList();

	// The test cohort subscribes to deactivated users mined from the current search results so
	// author data arriving after this filter renders immediately updates its options. Control uses
	// stable no-op functions: hooks cannot be called conditionally, but rollback must not attach a
	// cache subscription or read its snapshot.
	const minedUsersVersion = useSyncExternalStore(
		isDeactivatedContributorEnabled ? subscribeToMinedDeactivatedUsers : NOOP_SUBSCRIBE,
		isDeactivatedContributorEnabled ? getMinedDeactivatedUsersVersion : DISABLED_SNAPSHOT,
		isDeactivatedContributorEnabled ? getMinedDeactivatedUsersVersion : DISABLED_SNAPSHOT,
	);
	const isConfluenceInSearchScope = queryParams.product
		? queryParams.product === ProductKeys.Confluence
		: availableProducts.includes(ProductKeys.Confluence);
	const deactivatedUsers = useMemo(
		() =>
			isDeactivatedContributorEnabled && isConfluenceInSearchScope
				? getMinedDeactivatedUsers()
				: EMPTY_USERS,
		// eslint-disable-next-line react-hooks/exhaustive-deps -- The external-store version intentionally invalidates this module-cache read.
		[isDeactivatedContributorEnabled, isConfluenceInSearchScope, minedUsersVersion],
	);

	// Decorate the selected / suggested groups so the "(Deactivated)" affordance stays visible.
	const selectedOptions = useMemo(
		() =>
			isDeactivatedContributorEnabled
				? applyDeactivatedUsers(rawSelectedOptions, deactivatedUsers, formatMessage)
				: rawSelectedOptions,
		[isDeactivatedContributorEnabled, rawSelectedOptions, deactivatedUsers, formatMessage],
	);
	const suggestedOptions = useMemo(
		() =>
			isDeactivatedContributorEnabled
				? applyDeactivatedUsers(rawSuggestedOptions, deactivatedUsers, formatMessage)
				: rawSuggestedOptions,
		[isDeactivatedContributorEnabled, rawSuggestedOptions, deactivatedUsers, formatMessage],
	);

	// Decorate the lookup results AND inject deactivated users matching the typed query that URS
	// didn't return (so they can be selected). Injected options reflect current selection state so
	// the toggle can deselect them.
	const lookupOptionsWithDeactivated = useMemo(() => {
		if (!isDeactivatedContributorEnabled || deactivatedUsers.length === 0) {
			return rawOptions;
		}
		const selectedValues = new Set(selectedOptions.map((option) => option.value));
		return applyDeactivatedUsers(rawOptions, deactivatedUsers, formatMessage, {
			matchingUsers: findMinedDeactivatedUsers(lookupInput ?? ''),
			selectedValues,
		});
	}, [
		isDeactivatedContributorEnabled,
		rawOptions,
		deactivatedUsers,
		formatMessage,
		selectedOptions,
		lookupInput,
	]);

	// Exposure point: a user opens the Contributor dropdown while Confluence is in search scope.
	// The query only fetches author data for the test cohort, so conditioning exposure on mined
	// users would exclude every control user and bias the experiment. `getExperimentValue` dedupes
	// repeated exposures.
	const onDropdownOpen = () => {
		if (isConfluenceInSearchScope) {
			serveAppendDeactivatedAccountToContributorList({ fireExperimentExposure: true });
		}
		loadInitialOptions();
	};

	const onSelectChange = (option: SelectFilterOption | SelectFilterOption[]) => {
		setOption(option);
		const changedOption = Array.isArray(option) ? option[0] : option;
		if (isDeactivatedContributorEnabled && changedOption) {
			onFilterChange?.({
				isSelected: changedOption.isSelected,
				attributes: {
					isDeactivatedUser: Boolean(changedOption.metadata?.isDeactivated),
				},
			});
		} else {
			onFilterChange?.(option);
		}
	};

	if (!filterData) {
		return null;
	}

	const groupedOptions = [
		{
			label: formatMessage(i18n.confluenceFilterContributorLabelSelected),
			options: selectedOptions,
		},
		{
			label: formatMessage(i18n.confluenceFilterContributorLabelUnselected),
			options: suggestedOptions,
		},
	];

	const label =
		fit === 'compact' && selectedOptions.length > 0 ? '' : formatMessage(i18n.contributor);

	return (
		<SelectFilter
			contentFit={fit}
			canClearFilter
			checkboxIndicators
			onDropdownOpen={onDropdownOpen}
			onDropdownClose={clearLookupInput}
			onSearchInputChange={setLookupInput}
			onSelectChange={onSelectChange}
			options={isLookupActive ? lookupOptionsWithDeactivated : groupedOptions}
			selectedOptions={selectedOptions}
			label={label}
			placeholder={formatMessage(i18n.confluenceFilterContributorPlaceholder)}
			filterOrControlId={'contributor'}
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			formatOptionLabel={formatAvatarOptionLabel}
			onClearOptions={() => {
				clearOptions();
				onClearOptions?.({ filterId: contributorFilter.id });
			}}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			iconBefore={
				<FilterProductIcon
					primaryProduct={primaryProduct}
					showPrimaryProductIcon={showPrimaryProductIcon}
				/>
			}
			hideOnHoverOut={hideOnHoverOut}
			testId="contributor-filter"
		/>
	);
};

export default ContributorFilter;
